import { useCallback, useRef, useState } from 'react';
export const useEmojiPicker = (inputRef, setInput) => {
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const savedRangeRef = useRef(null);
    const saveCursorPosition = useCallback(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    }, []);
    const isRangeInsideInput = useCallback((range) => {
        if (!inputRef.current || !range) {
            return false;
        }
        // Check if both start and end containers are inside the input element
        return (inputRef.current.contains(range.startContainer) &&
            inputRef.current.contains(range.endContainer));
    }, [inputRef]);
    const restoreCursorPosition = useCallback(() => {
        if (savedRangeRef.current && inputRef.current) {
            inputRef.current.focus();
            const selection = window.getSelection();
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            selection === null || selection === void 0 ? void 0 : selection.addRange(savedRangeRef.current);
        }
    }, [inputRef]);
    /**
     * @param emojiObject - The emoji object to insert
     * @returns void
     * @description Handles the click event for the emoji picker so we can insert the emoji into the previous position.
     */
    const handleEmojiClick = useCallback((emojiObject) => {
        if (inputRef.current) {
            // Check if we have a saved range and it's inside the input element
            if (savedRangeRef.current &&
                isRangeInsideInput(savedRangeRef.current)) {
                restoreCursorPosition();
                // Use the restored range to insert the emoji
                const range = savedRangeRef.current;
                range.deleteContents(); // Remove any selected text
                // Create a text node with the emoji
                const emojiNode = document.createTextNode(emojiObject.emoji);
                range.insertNode(emojiNode);
                // Move cursor after the emoji
                range.setStartAfter(emojiNode);
                range.setEndAfter(emojiNode);
                const selection = window.getSelection();
                selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
                selection === null || selection === void 0 ? void 0 : selection.addRange(range);
                // Update React state for saved range case
                setInput(inputRef.current.textContent || '');
                // Clear the saved range
                savedRangeRef.current = null;
            }
            else {
                // Fallback: if no saved position or range is outside input, focus input and append to the end
                inputRef.current.focus();
                // Wait a bit for focus to be established, then insert emoji
                setTimeout(() => {
                    if (inputRef.current) {
                        // Get current content and append emoji
                        const currentContent = inputRef.current.textContent || '';
                        inputRef.current.textContent = currentContent + emojiObject.emoji;
                        // Move cursor to the end after the emoji
                        const range = document.createRange();
                        const sel = window.getSelection();
                        // Ensure we have content to work with
                        if (inputRef.current.firstChild) {
                            range.setStart(inputRef.current.firstChild, inputRef.current.textContent.length);
                            range.setEnd(inputRef.current.firstChild, inputRef.current.textContent.length);
                        }
                        else {
                            range.selectNodeContents(inputRef.current);
                            range.collapse(false);
                        }
                        sel === null || sel === void 0 ? void 0 : sel.removeAllRanges();
                        sel === null || sel === void 0 ? void 0 : sel.addRange(range);
                        // Update React state
                        setInput(inputRef.current.textContent || '');
                    }
                }, 10);
            }
        }
        setIsEmojiPickerOpen(false);
    }, [inputRef, restoreCursorPosition, setInput, isRangeInsideInput]);
    return {
        handleEmojiClick,
        saveCursorPosition,
        restoreCursorPosition,
        isEmojiPickerOpen,
        setIsEmojiPickerOpen,
    };
};
